import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Loader, Users, Shield, Monitor, Download, Globe } from 'lucide-react';
import axios from 'axios';
import { status as isSignedIn, getToken } from '../../hooks/AuthProvider';
import { isApplePlatform } from '../../utils/platform';

const API = process.env.REACT_APP_API_URL ?? '';

interface InvitePreview {
    creatorDisplayName: string | null;
    creatorUsername: string | null;
    creatorProfilePic: string | null;
    label: string;
    permissions: string[];
    expiresAt: string;
}

type PageStatus = 'loading' | 'ready' | 'acting' | 'accepted' | 'declined' | 'error';

const PERMISSION_LABELS: Record<string, string> = {
    create_post:     'Create Posts',
    edit_post:       'Edit Posts',
    delete_post:     'Delete Posts',
    manage_comments: 'Manage Comments',
    view_analytics:  'View Analytics',
    manage_stream:   'Manage Stream',
    manage_messages: 'Manage Messages',
};

function OpenInApp({ deepLinkPath }: { deepLinkPath: string }) {
    const [launched, setLaunched] = useState(false);
    const apple = isApplePlatform();

    const handleOpen = () => {
        window.location.href = `guardedcontent://${deepLinkPath}`;
        setLaunched(true);
    };

    return (
        <div className="w-full rounded-xl border border-red-500/30 bg-red-950/20 p-4 space-y-3">
            <div className="flex items-center gap-2">
                <Monitor className="h-5 w-5 text-red-400 flex-shrink-0" />
                <div>
                    <p className="text-sm font-semibold text-white">Open in Desktop App</p>
                    <p className="text-xs text-zinc-400">Best experience in the GuardedContent app</p>
                </div>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row">
                <button
                    onClick={handleOpen}
                    className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-700 transition-colors"
                >
                    <Monitor className="h-4 w-4" />
                    Open in Desktop App
                </button>

                {apple && (
                    <a
                        href="/downloads"
                        className="flex items-center justify-center gap-2 rounded-lg border border-zinc-600 px-4 py-2.5 text-sm font-medium text-zinc-300 hover:bg-zinc-700 transition-colors"
                    >
                        <Download className="h-4 w-4" />
                        Download the App
                    </a>
                )}

                {!apple && (
                    <a
                        href="/downloads"
                        className="flex items-center justify-center gap-2 rounded-lg border border-zinc-600 px-4 py-2.5 text-sm font-medium text-zinc-300 hover:bg-zinc-700 transition-colors"
                    >
                        <Download className="h-4 w-4" />
                        Download the App
                    </a>
                )}
            </div>

            {launched && (
                <p className="text-xs text-zinc-500">
                    If the app didn't open,{' '}
                    <a href="/downloads" className="text-red-400 hover:underline">download GuardedContent</a>.
                </p>
            )}
        </div>
    );
}

export default function AcceptTeamInvite() {
    const [searchParams] = useSearchParams();
    const [pageStatus, setPageStatus] = useState<PageStatus>('loading');
    const [invite, setInvite] = useState<InvitePreview | null>(null);
    const [errorMsg, setErrorMsg] = useState('');
    const token = searchParams.get('token') ?? '';
    const signedIn = isSignedIn();

    useEffect(() => {
        if (!token) {
            setPageStatus('error');
            setErrorMsg('Missing invitation token. Please check your email link.');
            return;
        }

        let cancelled = false;
        (async () => {
            try {
                const { data } = await axios.get(`${API}api/v1/team/invite/preview`, {
                    params: { token },
                });
                if (cancelled) return;
                setInvite(data);
                setPageStatus('ready');
            } catch (err: any) {
                if (cancelled) return;
                const msg = err?.response?.data?.error ?? 'Could not load the invitation.';
                setPageStatus('error');
                setErrorMsg(msg);
            }
        })();

        return () => { cancelled = true; };
    }, [token]);

    const handleAccept = async () => {
        setPageStatus('acting');
        try {
            await axios.post(
                `${API}api/v1/team/invite/accept`,
                { token },
                { headers: { 'x-auth-token': getToken() } }
            );
            setPageStatus('accepted');
        } catch (err: any) {
            const msg = err?.response?.data?.error ?? 'Failed to accept invitation.';
            setPageStatus('error');
            setErrorMsg(msg);
        }
    };

    const handleDecline = async () => {
        setPageStatus('acting');
        try {
            await axios.post(
                `${API}api/v1/team/invite/decline`,
                { token },
                { headers: { 'x-auth-token': getToken() } }
            );
            setPageStatus('declined');
        } catch (err: any) {
            const msg = err?.response?.data?.error ?? 'Failed to decline invitation.';
            setPageStatus('error');
            setErrorMsg(msg);
        }
    };

    const loginUrl = `/login?redirect=${encodeURIComponent(`/team/invite?token=${token}`)}`;

    return (
        <main className="flex min-h-screen items-center justify-center bg-[#0B0E11] p-4">
            <div className="w-full max-w-md overflow-hidden rounded-xl bg-zinc-900/90 border border-zinc-800 shadow-2xl backdrop-blur-sm">
                {/* Header */}
                <div className="px-6 pt-6 pb-4 text-center border-b border-zinc-800">
                    <div className="mx-auto mb-1 flex items-center justify-center">
                        <span className="text-2xl font-bold tracking-tighter text-white">GUARDED</span>
                        <span className="text-2xl font-bold tracking-tighter text-red-500">CONTENT</span>
                    </div>
                    <p className="text-sm text-zinc-400">Team Invitation</p>
                </div>

                <div className="p-6 flex flex-col items-center gap-5">
                    {/* Loading */}
                    {pageStatus === 'loading' && (
                        <>
                            <Loader className="h-10 w-10 animate-spin text-red-500" />
                            <p className="text-zinc-400 text-sm">Loading your invitation…</p>
                        </>
                    )}

                    {/* Acting */}
                    {pageStatus === 'acting' && (
                        <>
                            <Loader className="h-10 w-10 animate-spin text-red-500" />
                            <p className="text-zinc-400 text-sm">Processing…</p>
                        </>
                    )}

                    {/* Error */}
                    {pageStatus === 'error' && (
                        <>
                            <XCircle className="h-12 w-12 text-red-500" />
                            <div className="text-center space-y-1">
                                <p className="text-white font-semibold">Invitation Unavailable</p>
                                <p className="text-sm text-zinc-400">{errorMsg}</p>
                            </div>
                            <Link to="/" className="text-sm text-red-400 hover:underline">
                                Go to homepage
                            </Link>
                        </>
                    )}

                    {/* Accepted */}
                    {pageStatus === 'accepted' && (
                        <>
                            <CheckCircle className="h-12 w-12 text-green-500" />
                            <div className="text-center space-y-1">
                                <p className="text-white font-semibold">You joined the team!</p>
                                <p className="text-sm text-zinc-400">
                                    You can now act on behalf of{' '}
                                    <strong>{invite?.creatorDisplayName ?? 'this creator'}</strong>.
                                </p>
                            </div>
                            <Link
                                to="/app/settings"
                                className="text-sm text-red-400 hover:underline"
                            >
                                Settings → Team
                            </Link>
                        </>
                    )}

                    {/* Declined */}
                    {pageStatus === 'declined' && (
                        <>
                            <XCircle className="h-12 w-12 text-zinc-500" />
                            <div className="text-center space-y-1">
                                <p className="text-white font-semibold">Invitation declined.</p>
                                <p className="text-sm text-zinc-400">You can close this page.</p>
                            </div>
                        </>
                    )}

                    {/* Ready — invite card */}
                    {pageStatus === 'ready' && invite && (
                        <>
                            {/* Creator info */}
                            <div className="flex flex-col items-center gap-2 text-center">
                                {invite.creatorProfilePic ? (
                                    <img
                                        src={invite.creatorProfilePic}
                                        alt=""
                                        className="h-16 w-16 rounded-full object-cover ring-2 ring-red-500/40"
                                    />
                                ) : (
                                    <div className="h-16 w-16 rounded-full bg-zinc-800 flex items-center justify-center ring-2 ring-red-500/20">
                                        <Users className="h-8 w-8 text-zinc-500" />
                                    </div>
                                )}

                                <div>
                                    <p className="text-white font-semibold text-base">
                                        {invite.creatorDisplayName ?? 'A creator'}
                                        {invite.creatorUsername && (
                                            <span className="text-zinc-500 font-normal text-sm ml-1">
                                                @{invite.creatorUsername}
                                            </span>
                                        )}
                                    </p>
                                    <p className="text-sm text-zinc-400">
                                        invited you{invite.label ? (
                                            <> as <span className="text-red-400 font-medium">{invite.label}</span></>
                                        ) : ' to join their team'}
                                    </p>
                                </div>
                            </div>

                            {/* Permissions */}
                            {invite.permissions.length > 0 && (
                                <div className="w-full bg-zinc-800/60 rounded-lg p-3">
                                    <p className="text-xs text-zinc-400 mb-2 flex items-center gap-1">
                                        <Shield className="h-3 w-3" /> Permissions granted
                                    </p>
                                    <div className="flex flex-wrap gap-1.5">
                                        {invite.permissions.map(p => (
                                            <span
                                                key={p}
                                                className="inline-block bg-zinc-700 text-zinc-200 rounded-full px-2.5 py-0.5 text-xs"
                                            >
                                                {PERMISSION_LABELS[p] ?? p}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* PRIMARY: Open in Desktop App */}
                            <OpenInApp deepLinkPath={`team/invite?token=${token}`} />

                            {/* Divider */}
                            <div className="flex items-center gap-3 w-full">
                                <div className="flex-1 h-px bg-zinc-800" />
                                <span className="text-xs text-zinc-500 whitespace-nowrap">or respond in your browser</span>
                                <div className="flex-1 h-px bg-zinc-800" />
                            </div>

                            {/* SECONDARY: Accept / Decline (signed in) or Sign In link */}
                            {signedIn ? (
                                <div className="flex w-full gap-3">
                                    <button
                                        onClick={handleAccept}
                                        className="flex-1 rounded-lg bg-red-600 py-2.5 px-4 text-white text-sm font-semibold hover:bg-red-700 transition-colors"
                                    >
                                        Accept
                                    </button>
                                    <button
                                        onClick={handleDecline}
                                        className="flex-1 rounded-lg border border-zinc-700 py-2.5 px-4 text-zinc-300 text-sm font-medium hover:bg-zinc-800 transition-colors"
                                    >
                                        Decline
                                    </button>
                                </div>
                            ) : (
                                <a
                                    href={loginUrl}
                                    className="w-full flex items-center justify-center rounded-lg border border-zinc-700 py-2.5 px-4 text-zinc-300 text-sm font-medium hover:bg-zinc-800 transition-colors"
                                >
                                    Sign In to Accept
                                </a>
                            )}
                        </>
                    )}
                </div>
            </div>
        </main>
    );
}
